/**
 * PerformanceInsights - MA-ARCH-153 + MA-ARCH-154 + MA-ARCH-155
 * 
 * Frontend surface for the Performance Interpretation Layer.
 * Displays structured, explainable recommendations from agent metrics.
 * 
 * MA-ARCH-155 Features:
 * - "Context-adjusted" badges on normalized insights
 * - Raw vs adjusted comparison in detail view
 * - Normalization quality indicator
 * - Context difficulty label display
 * - Baseline scope used information
 * 
 * MA-ARCH-154 Features:
 * - Approve/Dismiss workflow buttons
 * - Create Action from approved insights
 * - Review status badges and indicators
 * - Dismissed insights hidden by default (filter toggle)
 * - Notes input for review actions
 * 
 * Original Features:
 * - Insights Summary Section (counts by type)
 * - Insights Feed/List with filtering
 * - Confidence and severity badges
 * - Explanation factors display
 * - Agent drilldown integration
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Lightbulb,
  TrendingUp,
  TrendingDown,
  Target,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Info,
  Loader2,
  User,
  FileText,
  GitBranch,
  Clock,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ArrowRight,
  ChevronRight,
  Zap,
  UserCog,
  Route,
  Search,
  Eye,
  EyeOff,
  Filter,
  RefreshCw,
  Award,
  AlertCircle,
  Minus,
  Check,
  X,
  Play,
  Archive,
  CheckCircle2,
  Ban,
  CircleDot,
  Scale,
  BarChart3,
  // MA-ARCH-157: Batch selection icons
  CheckSquare,
  Square,
  ListChecks,
  // MA-ARCH-158: History and trace icons
  History,
  Network,
  CalendarClock,
  Footprints,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { useTenant } from '@/contexts/TenantContext';
import { toast } from 'sonner';

// =============================================================================
// MA-ARCH-160: NORMALIZATION COVERAGE VISIBILITY
// =============================================================================

// Trust label configuration
const trustLabelConfig = {
  strong: {
    label: 'Strong',
    color: 'text-emerald-400',
    bgClass: 'bg-emerald-500/20 border-emerald-500/30',
    description: 'Insights compared within strong, similar context',
  },
  mixed: {
    label: 'Mixed',
    color: 'text-amber-400',
    bgClass: 'bg-amber-500/20 border-amber-500/30',
    description: 'Partial context matching; interpret with moderate confidence',
  },
  limited: {
    label: 'Limited',
    color: 'text-red-400',
    bgClass: 'bg-red-500/20 border-red-500/30',
    description: 'Insufficient context for strong comparisons',
  },
};

// Normalization quality colors
const normQualityColors = {
  well_normalized: 'bg-emerald-500',
  partially_normalized: 'bg-amber-500',
  insufficient_context_match: 'bg-red-500',
};

// Fallback scope display names
const scopeDisplayNames = {
  same_role_same_profit_path_same_funnel: 'Same Role + Path + Funnel',
  same_role_same_profit_path: 'Same Role + Path',
  same_role_same_vertical: 'Same Role + Vertical',
  same_role_only: 'Same Role Only',
  raw_no_normalization: 'No Normalization',
};

// Compact normalization coverage bar for insights header
function NormalizationCoverageBar({ coverage }) {
  if (!coverage?.overall) return null;
  
  const overall = coverage.overall;
  const dist = overall.distribution;
  const trustConf = trustLabelConfig[overall.trust_label] || trustLabelConfig.limited;
  
  // Don't show if no insights
  if (overall.total_insights === 0) return null;
  
  return (
    <TooltipProvider>
      <div className="flex items-center gap-3 text-xs mt-2" data-testid="normalization-coverage-bar">
        <span className="text-slate-500">Context Quality:</span>
        
        {/* Distribution bar */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex h-2 w-24 rounded-full overflow-hidden bg-slate-700/50">
              {dist.well_normalized_pct > 0 && (
                <div 
                  className="bg-emerald-500 transition-all"
                  style={{ width: `${dist.well_normalized_pct * 100}%` }}
                />
              )}
              {dist.partial_pct > 0 && (
                <div 
                  className="bg-amber-500 transition-all"
                  style={{ width: `${dist.partial_pct * 100}%` }}
                />
              )}
              {dist.insufficient_pct > 0 && (
                <div 
                  className="bg-red-500 transition-all"
                  style={{ width: `${dist.insufficient_pct * 100}%` }}
                />
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="bg-slate-800 border-slate-700 max-w-xs">
            <div className="space-y-1 text-xs">
              <p className="font-medium text-slate-200">Normalization Quality Distribution</p>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-slate-300">Well-normalized: {(dist.well_normalized_pct * 100).toFixed(0)}%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-slate-300">Partial: {(dist.partial_pct * 100).toFixed(0)}%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-slate-300">Insufficient: {(dist.insufficient_pct * 100).toFixed(0)}%</span>
              </div>
              <p className="text-slate-500 pt-1 text-[10px]">
                "Well-normalized" = compared within strong, similar context
              </p>
            </div>
          </TooltipContent>
        </Tooltip>
        
        {/* Trust label badge */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge className={cn("text-[10px] border cursor-help", trustConf.bgClass)}>
              {trustConf.label}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="bg-slate-800 border-slate-700 max-w-xs">
            <p className="text-xs text-slate-300">{trustConf.description}</p>
            {overall.low_volume_warning && (
              <p className="text-[10px] text-amber-400 mt-1">
                ⚠ Based on limited data ({overall.total_insights} insights)
              </p>
            )}
          </TooltipContent>
        </Tooltip>
        
        {/* Percentage summary */}
        <span className="text-slate-500">
          {(dist.well_normalized_pct * 100).toFixed(0)}% well-normalized
        </span>
      </div>
    </TooltipProvider>
  );
}

// Agent drilldown normalization section
function AgentNormalizationSection({ agentCoverage }) {
  if (!agentCoverage) return null;
  
  const dist = agentCoverage.distribution;
  const trustConf = trustLabelConfig[agentCoverage.trust_label] || trustLabelConfig.limited;
  
  return (
    <TooltipProvider>
      <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05] space-y-2" data-testid="agent-normalization-section">
        <div className="flex items-center justify-between">
          <h5 className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-2">
            <Scale className="h-3 w-3" />
            Normalization Context
          </h5>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge className={cn("text-[10px] border cursor-help", trustConf.bgClass)}>
                {trustConf.label}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="left" className="bg-slate-800 border-slate-700 max-w-xs">
              <p className="text-xs text-slate-300">{trustConf.description}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        
        {/* Distribution */}
        <div className="space-y-1 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Well-normalized</span>
            <span className="text-emerald-400">{(dist.well_normalized_pct * 100).toFixed(0)}%</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Partial</span>
            <span className="text-amber-400">{(dist.partial_pct * 100).toFixed(0)}%</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Insufficient</span>
            <span className={cn(
              dist.insufficient_pct > 0.1 ? "text-red-400" : "text-slate-400"
            )}>{(dist.insufficient_pct * 100).toFixed(0)}%</span>
          </div>
        </div>
        
        {/* Top baseline scopes */}
        {agentCoverage.top_baseline_scopes?.length > 0 && (
          <div className="pt-2 border-t border-white/[0.05]">
            <span className="text-[10px] text-slate-500">Common baselines:</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {agentCoverage.top_baseline_scopes.slice(0, 2).map((scope) => (
                <span key={scope} className="text-[10px] text-slate-400 bg-slate-800/50 px-1.5 py-0.5 rounded">
                  {scopeDisplayNames[scope] || scope}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Warning for elevated insufficient */}
        {agentCoverage.has_elevated_insufficient && (
          <div className="flex items-center gap-1 text-[10px] text-amber-400 pt-1">
            <AlertTriangle className="h-3 w-3" />
            <span>Elevated insufficient-context share</span>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}

// =============================================================================
// CONFIGURATION
// =============================================================================

const insightTypeConfig = {
  high_performer: { label: 'High Performer', icon: Award, color: 'text-emerald-400', bgClass: 'bg-emerald-500/20 border-emerald-500/30' },
  underperformer: { label: 'Underperformer', icon: TrendingDown, color: 'text-red-400', bgClass: 'bg-red-500/20 border-red-500/30' },
  script_strength: { label: 'Script Strength', icon: FileText, color: 'text-blue-400', bgClass: 'bg-blue-500/20 border-blue-500/30' },
  script_weakness: { label: 'Script Weakness', icon: FileText, color: 'text-amber-400', bgClass: 'bg-amber-500/20 border-amber-500/30' },
  profit_path_strength: { label: 'Profit Path Strength', icon: GitBranch, color: 'text-emerald-400', bgClass: 'bg-emerald-500/20 border-emerald-500/30' },
  routing_opportunity: { label: 'Routing Opportunity', icon: Route, color: 'text-cyan-400', bgClass: 'bg-cyan-500/20 border-cyan-500/30' },
  coaching_opportunity: { label: 'Coaching Opportunity', icon: UserCog, color: 'text-violet-400', bgClass: 'bg-violet-500/20 border-violet-500/30' },
  lead_quality_mismatch: { label: 'Lead Quality Mismatch', icon: Search, color: 'text-amber-400', bgClass: 'bg-amber-500/20 border-amber-500/30' },
  followup_speed_issue: { label: 'Follow-up Speed Issue', icon: Clock, color: 'text-red-400', bgClass: 'bg-red-500/20 border-red-500/30' },
  data_capture_issue: { label: 'Data Capture Issue', icon: AlertCircle, color: 'text-amber-400', bgClass: 'bg-amber-500/20 border-amber-500/30' },
  show_rate_issue: { label: 'Show Rate Issue', icon: Eye, color: 'text-amber-400', bgClass: 'bg-amber-500/20 border-amber-500/30' },
  close_rate_issue: { label: 'Close Rate Issue', icon: Target, color: 'text-red-400', bgClass: 'bg-red-500/20 border-red-500/30' },
  // MA-ARCH-156: New execution quality insight types
  execution_quality_issue: { label: 'Execution Quality Issue', icon: AlertTriangle, color: 'text-red-400', bgClass: 'bg-red-500/20 border-red-500/30' },
  strong_execution_underperformance: { label: 'Strong Execution, Poor Outcome', icon: Search, color: 'text-amber-400', bgClass: 'bg-amber-500/20 border-amber-500/30' },
  dangerous_success: { label: 'Fragile Success', icon: AlertTriangle, color: 'text-amber-400', bgClass: 'bg-amber-500/20 border-amber-500/30' },
  process_fragility: { label: 'Process Fragility', icon: AlertTriangle, color: 'text-amber-400', bgClass: 'bg-amber-500/20 border-amber-500/30' },
};

const severityConfig = {
  high: { label: 'High', color: 'text-red-400', bgClass: 'bg-red-500/20 border-red-500/30' },
  medium: { label: 'Medium', color: 'text-amber-400', bgClass: 'bg-amber-500/20 border-amber-500/30' },
  info: { label: 'Info', color: 'text-blue-400', bgClass: 'bg-blue-500/20 border-blue-500/30' },
};

const confidenceConfig = {
  high: { label: 'High', icon: ShieldCheck, color: 'text-emerald-400' },
  medium: { label: 'Medium', icon: Shield, color: 'text-amber-400' },
  low: { label: 'Low', icon: ShieldAlert, color: 'text-slate-400' },
};

const directionConfig = {
  positive: { icon: TrendingUp, color: 'text-emerald-400' },
  negative: { icon: TrendingDown, color: 'text-red-400' },
  neutral: { icon: Minus, color: 'text-slate-400' },
};

const actionTypeConfig = {
  coach_agent: { label: 'Coach Agent', icon: UserCog, color: 'text-violet-400' },
  route_more_leads: { label: 'Route More Leads', icon: Route, color: 'text-cyan-400' },
  reduce_routing: { label: 'Reduce Routing', icon: TrendingDown, color: 'text-amber-400' },
  investigate_input_quality: { label: 'Investigate', icon: Search, color: 'text-amber-400' },
  review_script_usage: { label: 'Review Script', icon: FileText, color: 'text-blue-400' },
  review_followup_process: { label: 'Review Follow-up', icon: Clock, color: 'text-red-400' },
  monitor_only: { label: 'Monitor', icon: Eye, color: 'text-slate-400' },
  // MA-ARCH-156: New action types
  review_process: { label: 'Review Process', icon: Search, color: 'text-amber-400' },
  investigate_execution: { label: 'Investigate Execution', icon: Search, color: 'text-amber-400' },
};

// MA-ARCH-154: Review status configuration
const reviewStatusConfig = {
  pending_review: { 
    label: 'Pending Review', 
    icon: CircleDot, 
    color: 'text-amber-400', 
    bgClass: 'bg-amber-500/20 border-amber-500/30' 
  },
  approved: { 
    label: 'Approved', 
    icon: CheckCircle2, 
    color: 'text-emerald-400', 
    bgClass: 'bg-emerald-500/20 border-emerald-500/30' 
  },
  dismissed: { 
    label: 'Dismissed', 
    icon: Ban, 
    color: 'text-slate-400', 
    bgClass: 'bg-slate-500/20 border-slate-500/30' 
  },
  action_created: { 
    label: 'Action Created', 
    icon: Zap, 
    color: 'text-cyan-400', 
    bgClass: 'bg-cyan-500/20 border-cyan-500/30' 
  },
};

// MA-ARCH-155: Normalization quality configuration
const normalizationQualityConfig = {
  well_normalized: {
    label: 'Well Normalized',
    icon: Scale,
    color: 'text-emerald-400',
    bgClass: 'bg-emerald-500/20 border-emerald-500/30',
    description: 'Strong context match with adequate sample'
  },
  partially_normalized: {
    label: 'Partially Normalized',
    icon: Scale,
    color: 'text-amber-400',
    bgClass: 'bg-amber-500/20 border-amber-500/30',
    description: 'Moderate context match; interpret with caution'
  },
  insufficient_context_match: {
    label: 'Raw Only',
    icon: BarChart3,
    color: 'text-slate-400',
    bgClass: 'bg-slate-500/20 border-slate-500/30',
    description: 'Insufficient comparable peers for normalization'
  },
};

// MA-ARCH-155: Context difficulty configuration
const contextDifficultyConfig = {
  favorable: { label: 'Favorable', color: 'text-emerald-400' },
  easier_than_average: { label: 'Easier', color: 'text-cyan-400' },
  mixed: { label: 'Mixed', color: 'text-slate-400' },
  harder_than_average: { label: 'Harder', color: 'text-amber-400' },
  unknown: { label: 'Unknown', color: 'text-slate-500' },
};

// =============================================================================
// API
// =============================================================================

async function fetchInsights(params) {
  const queryParams = new URLSearchParams();
  queryParams.append('period', params.period || 'mtd');
  if (params.insight_type) queryParams.append('insight_type', params.insight_type);
  if (params.confidence) queryParams.append('confidence', params.confidence);
  if (params.severity) queryParams.append('severity', params.severity);
  if (params.action_type) queryParams.append('action_type', params.action_type);
  if (params.review_status) queryParams.append('review_status', params.review_status);
  if (params.include_dismissed) queryParams.append('include_dismissed', 'true');
  if (params.tenant_id) queryParams.append('tenant_id', params.tenant_id);
  
  const response = await api.get(`/analytics/agent-performance/insights?${queryParams}`);
  return response.data;
}

// MA-ARCH-154: Review action APIs
async function approveInsight(insightId, notes = null) {
  const response = await api.post(`/analytics/agent-performance/insights/${insightId}/approve`, {
    notes,
  });
  return response.data;
}

async function dismissInsight(insightId, notes = null) {
  const response = await api.post(`/analytics/agent-performance/insights/${insightId}/dismiss`, {
    notes,
  });
  return response.data;
}

async function createActionFromInsight(insightId, workspaceId = null) {
  const response = await api.post(`/analytics/agent-performance/insights/${insightId}/create-action`, {
    workspace_id: workspaceId,
  });
  return response.data;
}

// =============================================================================
// MA-ARCH-157: BATCH OPERATIONS API
// =============================================================================

async function validateBatch(insightIds, operation, mode = 'grouped') {
  const response = await api.post('/analytics/agent-performance/insights/batch/validate', {
    insight_ids: insightIds,
    operation,
    mode,
  });
  return response.data;
}

async function batchApproveInsights(insightIds, notes = null) {
  const response = await api.post('/analytics/agent-performance/insights/batch/approve', {
    insight_ids: insightIds,
    notes,
  });
  return response.data;
}

async function batchDismissInsights(insightIds, notes = null) {
  const response = await api.post('/analytics/agent-performance/insights/batch/dismiss', {
    insight_ids: insightIds,
    notes,
  });
  return response.data;
}

async function batchCreateActions(insightIds, mode = 'grouped', notes = null) {
  const response = await api.post('/analytics/agent-performance/insights/batch/create-action', {
    insight_ids: insightIds,
    notes,
    mode,
  });
  return response.data;
}

// =============================================================================
// MA-ARCH-157: BATCH SELECTION HELPER
// =============================================================================

const BATCH_CONFIDENCE_MIN = 0.6;

function isInsightEligibleForBatch(insight) {
  // Cannot batch: dismissed, already has action
  if (insight.review_status === 'dismissed' || insight.review_status === 'action_created') {
    return false;
  }
  // Cannot batch: low confidence
  if ((insight.confidence_score || 0) < BATCH_CONFIDENCE_MIN) {
    return false;
  }
  // Cannot batch: insufficient normalization
  if (insight.normalization_quality_label === 'insufficient_context_match') {
    return false;
  }
  return true;
}

function getEligibilityReason(insight) {
  if (insight.review_status === 'dismissed') return 'Dismissed';
  if (insight.review_status === 'action_created') return 'Already has action';
  if ((insight.confidence_score || 0) < BATCH_CONFIDENCE_MIN) return `Low confidence (${((insight.confidence_score || 0) * 100).toFixed(0)}%)`;
  if (insight.normalization_quality_label === 'insufficient_context_match') return 'Insufficient normalization';
  return null;
}

// =============================================================================
// INSIGHTS SUMMARY SECTION
// =============================================================================

function InsightsSummaryCards({ summary }) {
  if (!summary) return null;
  
  const cards = [
    { label: 'High Confidence', value: summary.high_confidence, icon: ShieldCheck, color: 'text-emerald-400' },
    { label: 'Coaching', value: summary.coaching_opportunities, icon: UserCog, color: 'text-violet-400' },
    { label: 'Routing', value: summary.routing_opportunities, icon: Route, color: 'text-cyan-400' },
    { label: 'Monitor Only', value: summary.monitor_only, icon: Eye, color: 'text-slate-400' },
  ];
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      {cards.map((card) => (
        <Card key={card.label} className="glass border-white/10">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400">{card.label}</p>
                <p className={cn("text-xl font-semibold", card.color)}>{card.value}</p>
              </div>
              <card.icon className={cn("h-6 w-6", card.color)} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// =============================================================================
// INSIGHT CARD - MA-ARCH-154 + MA-ARCH-155 Enhanced
// =============================================================================

function InsightCard({ insight, onViewDetails, onApprove, onDismiss, onCreateAction, isActionLoading, isSelected = false }) {
  const typeConf = insightTypeConfig[insight.insight_type] || insightTypeConfig.high_performer;
  const sevConf = severityConfig[insight.severity] || severityConfig.info;
  const confConf = confidenceConfig[insight.confidence_label] || confidenceConfig.medium;
  const dirConf = directionConfig[insight.direction] || directionConfig.neutral;
  const actionConf = actionTypeConfig[insight.recommended_action_type] || actionTypeConfig.monitor_only;
  const reviewConf = reviewStatusConfig[insight.review_status] || reviewStatusConfig.pending_review;
  
  // MA-ARCH-155: Normalization config
  const normConf = normalizationQualityConfig[insight.normalization_quality_label] || normalizationQualityConfig.insufficient_context_match;
  const diffConf = contextDifficultyConfig[insight.context_difficulty_label] || contextDifficultyConfig.unknown;
  const NormIcon = normConf.icon;
  
  const TypeIcon = typeConf.icon;
  const DirIcon = dirConf.icon;
  const ConfIcon = confConf.icon;
  const ActionIcon = actionConf.icon;
  const ReviewIcon = reviewConf.icon;
  
  const isPending = insight.review_status === 'pending_review';
  const isApproved = insight.review_status === 'approved';
  const isDismissed = insight.review_status === 'dismissed';
  const hasAction = insight.review_status === 'action_created';
  
  // MA-ARCH-155: Check if insight is normalized
  const isNormalized = insight.normalization_quality_label && 
    insight.normalization_quality_label !== 'insufficient_context_match';
  
  return (
    <Card 
      className={cn(
        "glass border-white/10 hover:border-white/20 transition-colors",
        isDismissed && "opacity-60",
        isSelected && "border-blue-500/50 ring-1 ring-blue-500/30"
      )}
      data-testid={`insight-card-${insight.id}`}
    >
      <CardContent className={cn("p-4", isSelected && "pl-10")}>
        {/* Header Row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className={cn("p-2 rounded-lg", typeConf.bgClass)}>
              <TypeIcon className={cn("h-4 w-4", typeConf.color)} />
            </div>
            <div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge className={cn("text-[10px] border", typeConf.bgClass)}>
                  {typeConf.label}
                </Badge>
                {/* MA-ARCH-154: Review Status Badge */}
                <Badge className={cn("text-[10px] border", reviewConf.bgClass)} data-testid={`insight-status-${insight.id}`}>
                  <ReviewIcon className="h-2.5 w-2.5 mr-1" />
                  {reviewConf.label}
                </Badge>
                {/* MA-ARCH-155: Context-Adjusted Badge */}
                {isNormalized && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Badge className={cn("text-[10px] border", normConf.bgClass)} data-testid={`insight-normalized-${insight.id}`}>
                          <NormIcon className="h-2.5 w-2.5 mr-1" />
                          Context-Adjusted
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <div className="space-y-1">
                          <p className="text-xs font-medium">{normConf.label}</p>
                          <p className="text-xs text-slate-400">{normConf.description}</p>
                          {insight.baseline_scope_used && (
                            <p className="text-xs text-slate-400">Scope: {insight.baseline_scope_used.replace(/_/g, ' ')}</p>
                          )}
                          {insight.comparison_context_count && (
                            <p className="text-xs text-slate-400">Compared to {insight.comparison_context_count} peers</p>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {insight.agent_name && (
                <p className="text-xs text-slate-400 mt-0.5">
                  <User className="h-3 w-3 inline mr-1" />
                  {insight.agent_name}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* MA-ARCH-155: Context Difficulty Badge */}
            {insight.context_difficulty_label && insight.context_difficulty_label !== 'unknown' && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge variant="outline" className={cn("text-[10px]", diffConf.color)}>
                      {diffConf.label}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Context difficulty: {diffConf.label}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {/* MA-ARCH-156: Field Quality Score Badge - Only show when relevant */}
            {insight.field_quality_score != null && insight.field_quality_confidence >= 0.4 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-[10px]",
                        insight.field_quality_score >= 80 ? "text-emerald-400 border-emerald-500/30" :
                        insight.field_quality_score >= 60 ? "text-amber-400 border-amber-500/30" :
                        "text-red-400 border-red-500/30"
                      )}
                      data-testid={`field-quality-badge-${insight.id}`}
                    >
                      <Target className="h-2.5 w-2.5 mr-1" />
                      FQ: {insight.field_quality_score.toFixed(0)}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium">Execution Quality: {insight.field_quality_score.toFixed(1)}</p>
                      <div className="text-xs text-slate-400 space-y-0.5">
                        <p>Confidence: {insight.field_quality_confidence_level || 'N/A'}</p>
                        <p>Sample size: {insight.field_quality_sample_size || 'N/A'} interactions</p>
                      </div>
                      {insight.diagnostic_conclusion && (
                        <p className="text-xs text-slate-300 pt-1 border-t border-white/10">
                          Diagnosis: {insight.diagnostic_conclusion.replace(/_/g, ' ')}
                        </p>
                      )}
                      {insight.field_quality_top_weaknesses && insight.field_quality_top_weaknesses.length > 0 && (
                        <div className="pt-1 border-t border-white/10">
                          <p className="text-[10px] text-slate-400 mb-0.5">Top Weakness:</p>
                          <p className="text-xs text-slate-300">
                            {insight.field_quality_top_weaknesses[0].dimension?.replace(/_/g, ' ')}
                          </p>
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <Badge className={cn("text-[10px] border", sevConf.bgClass)}>
              {sevConf.label}
            </Badge>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <ConfIcon className={cn("h-4 w-4", confConf.color)} />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Confidence: {confConf.label} ({(insight.confidence_score * 100).toFixed(0)}%)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        
        {/* Headline */}
        <h4 
          className="text-sm font-medium text-slate-100 mb-1 cursor-pointer hover:text-white"
          onClick={() => onViewDetails(insight)}
        >
          {insight.headline}
        </h4>
        <p className="text-xs text-slate-400 mb-3 line-clamp-2">{insight.summary}</p>
        
        {/* Key Metric */}
        <div className="flex items-center justify-between p-2 rounded bg-white/[0.02] border border-white/[0.05] mb-3">
          <div className="flex items-center gap-2">
            <DirIcon className={cn("h-4 w-4", dirConf.color)} />
            <span className="text-xs text-slate-400">{insight.metric_primary_name?.replace(/_/g, ' ')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={cn("text-sm font-medium", dirConf.color)}>
              {insight.metric_primary_formatted || insight.metric_primary_value}
            </span>
            {insight.baseline_formatted && (
              <span className="text-xs text-slate-500">
                vs {insight.baseline_formatted}
              </span>
            )}
          </div>
        </div>
        
        {/* Recommended Action */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ActionIcon className={cn("h-4 w-4", actionConf.color)} />
            <span className="text-xs text-slate-300">{insight.recommended_action_label}</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 px-2 text-xs"
            onClick={() => onViewDetails(insight)}
          >
            Details
            <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
        
        {/* MA-ARCH-154: Action Buttons */}
        <div className="flex items-center gap-2 pt-2 border-t border-white/[0.05]">
          {isPending && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 h-8 text-xs bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"
                onClick={(e) => { e.stopPropagation(); onApprove(insight); }}
                disabled={isActionLoading}
                data-testid={`approve-btn-${insight.id}`}
              >
                <Check className="h-3 w-3 mr-1" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 h-8 text-xs bg-slate-500/10 border-slate-500/30 text-slate-400 hover:bg-slate-500/20"
                onClick={(e) => { e.stopPropagation(); onDismiss(insight); }}
                disabled={isActionLoading}
                data-testid={`dismiss-btn-${insight.id}`}
              >
                <X className="h-3 w-3 mr-1" />
                Dismiss
              </Button>
            </>
          )}
          {isApproved && (
            <Button
              size="sm"
              className="w-full h-8 text-xs bg-cyan-500/20 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/30"
              onClick={(e) => { e.stopPropagation(); onCreateAction(insight); }}
              disabled={isActionLoading}
              data-testid={`create-action-btn-${insight.id}`}
            >
              {isActionLoading ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Zap className="h-3 w-3 mr-1" />
              )}
              Create Action
            </Button>
          )}
          {hasAction && (
            <div className="w-full text-center text-xs text-cyan-400 flex items-center justify-center gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Action created: {insight.linked_action_id}
            </div>
          )}
          {isDismissed && (
            <div className="w-full text-center text-xs text-slate-400 flex items-center justify-center gap-1">
              <Archive className="h-3 w-3" />
              Dismissed
              {insight.review_notes && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-3 w-3 ml-1" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">{insight.review_notes}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// INSIGHT DETAIL DIALOG - MA-ARCH-158: Enhanced with History & Trace Tabs
// =============================================================================

// Timeline event type icons and colors
const historyEventConfig = {
  insight_generated: { icon: Lightbulb, color: 'text-violet-400', bgClass: 'bg-violet-500/20' },
  insight_approved: { icon: CheckCircle, color: 'text-emerald-400', bgClass: 'bg-emerald-500/20' },
  insight_dismissed: { icon: XCircle, color: 'text-slate-400', bgClass: 'bg-slate-500/20' },
  insight_action_created: { icon: Zap, color: 'text-indigo-400', bgClass: 'bg-indigo-500/20' },
  batch_validated: { icon: Shield, color: 'text-amber-400', bgClass: 'bg-amber-500/20' },
  batch_approved: { icon: CheckCircle2, color: 'text-emerald-400', bgClass: 'bg-emerald-500/20' },
  batch_dismissed: { icon: Ban, color: 'text-slate-400', bgClass: 'bg-slate-500/20' },
  batch_action_created: { icon: Zap, color: 'text-indigo-400', bgClass: 'bg-indigo-500/20' },
  action_linked: { icon: GitBranch, color: 'text-cyan-400', bgClass: 'bg-cyan-500/20' },
};

// Format relative time
function formatRelativeTime(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Timeline entry component
function HistoryTimelineEntry({ event }) {
  const config = historyEventConfig[event.event_type] || historyEventConfig.insight_generated;
  const EventIcon = config.icon;
  
  return (
    <div className="flex items-start gap-3 py-2 border-b border-white/[0.05] last:border-0">
      <div className={cn("p-1.5 rounded", config.bgClass)}>
        <EventIcon className={cn("h-3 w-3", config.color)} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-300">{event.summary}</p>
        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
          <span>{event.actor_label || 'system'}</span>
          <span>•</span>
          <span>{formatRelativeTime(event.occurred_at)}</span>
          {event.linked_batch_id && (
            <>
              <span>•</span>
              <Badge className="text-[9px] bg-amber-500/10 border-amber-500/20">batch</Badge>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// History Tab Content
function InsightHistoryTab({ insightId }) {
  const { data: historyData, isLoading, error } = useQuery({
    queryKey: ['insight-history', insightId],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL || ''}/api/analytics/agent-performance/insights/${insightId}/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error('Failed to fetch history');
      return resp.json();
    },
    enabled: !!insightId,
    staleTime: 30000,
  });
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        <span className="ml-2 text-sm text-slate-400">Loading history...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-6">
        <AlertCircle className="h-8 w-8 text-amber-400 mx-auto mb-2" />
        <p className="text-sm text-slate-400">Unable to load history</p>
      </div>
    );
  }
  
  const events = historyData?.events || [];
  
  if (events.length === 0) {
    return (
      <div className="text-center py-6">
        <History className="h-8 w-8 text-slate-500 mx-auto mb-2" />
        <p className="text-sm text-slate-400">No history events yet</p>
        <p className="text-xs text-slate-500 mt-1">Events will appear as actions are taken</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-0" data-testid="insight-history-timeline">
      {events.map((event) => (
        <HistoryTimelineEntry key={event.event_id} event={event} />
      ))}
    </div>
  );
}

// Decision Trace Tab Content
function InsightTraceTab({ insightId }) {
  const { data: traceData, isLoading, error } = useQuery({
    queryKey: ['insight-trace', insightId],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL || ''}/api/analytics/agent-performance/insights/${insightId}/trace`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error('Failed to fetch trace');
      return resp.json();
    },
    enabled: !!insightId,
    staleTime: 30000,
  });
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        <span className="ml-2 text-sm text-slate-400">Loading trace...</span>
      </div>
    );
  }
  
  if (error || !traceData?.success) {
    return (
      <div className="text-center py-6">
        <Network className="h-8 w-8 text-amber-400 mx-auto mb-2" />
        <p className="text-sm text-slate-400">Trace unavailable</p>
        <p className="text-xs text-slate-500 mt-1">This insight may not have been generated with trace data</p>
      </div>
    );
  }
  
  const trace = traceData.trace;
  if (!trace) {
    return (
      <div className="text-center py-6">
        <Footprints className="h-8 w-8 text-slate-500 mx-auto mb-2" />
        <p className="text-sm text-slate-400">No trace data</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="insight-decision-trace">
      {/* Current State Summary */}
      <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
        <h5 className="text-xs text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
          <CircleDot className="h-3 w-3" />
          Current State
        </h5>
        <div className="flex items-center gap-2">
          <Badge className={cn(
            "text-xs border",
            trace.current_status === 'approved' ? "bg-emerald-500/20 border-emerald-500/30" :
            trace.current_status === 'dismissed' ? "bg-slate-500/20 border-slate-500/30" :
            trace.current_status === 'action_created' ? "bg-indigo-500/20 border-indigo-500/30" :
            "bg-amber-500/20 border-amber-500/30"
          )}>
            {trace.current_status_label || trace.current_status?.replace(/_/g, ' ')}
          </Badge>
          {trace.action_created && trace.linked_action && (
            <span className="text-xs text-slate-400">
              → Action: {trace.linked_action.action_type}
            </span>
          )}
        </div>
      </div>
      
      {/* Generation Context */}
      {trace.generated_snapshot && (
        <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
          <h5 className="text-xs text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
            <Lightbulb className="h-3 w-3" />
            Generation Context
          </h5>
          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Generated</span>
              <span className="text-slate-300">{formatRelativeTime(trace.generated_at)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Confidence</span>
              <span className={cn(
                trace.generated_snapshot.confidence_score >= 0.7 ? "text-emerald-400" :
                trace.generated_snapshot.confidence_score >= 0.45 ? "text-amber-400" : "text-red-400"
              )}>
                {(trace.generated_snapshot.confidence_score * 100).toFixed(0)}%
              </span>
            </div>
            {trace.generated_snapshot.normalization_quality_label && (
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Normalization</span>
                <span className="text-slate-300">{trace.generated_snapshot.normalization_quality_label.replace(/_/g, ' ')}</span>
              </div>
            )}
            {trace.generated_snapshot.explanation_factors?.length > 0 && (
              <div className="pt-2 border-t border-white/[0.05]">
                <p className="text-slate-500 mb-1">Key Factors</p>
                {trace.generated_snapshot.explanation_factors.slice(0, 3).map((f, i) => (
                  <div key={i} className="flex items-start gap-1 text-slate-400">
                    <span className="text-slate-600">•</span>
                    <span>{f.description || f.metric_name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Review Decision */}
      {trace.review_decision && (
        <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
          <h5 className="text-xs text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
            {trace.review_decision.new_status === 'approved' ? (
              <CheckCircle className="h-3 w-3 text-emerald-400" />
            ) : (
              <XCircle className="h-3 w-3 text-slate-400" />
            )}
            Review Decision
          </h5>
          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Decision</span>
              <Badge className={cn(
                "text-[10px] border",
                trace.review_decision.new_status === 'approved' 
                  ? "bg-emerald-500/20 border-emerald-500/30" 
                  : "bg-slate-500/20 border-slate-500/30"
              )}>
                {trace.review_decision.new_status}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Reviewed by</span>
              <span className="text-slate-300">{trace.review_decision.reviewer_name || 'Unknown'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">When</span>
              <span className="text-slate-300">{formatRelativeTime(trace.reviewed_at)}</span>
            </div>
            {trace.review_decision.notes && (
              <div className="pt-2 border-t border-white/[0.05]">
                <p className="text-slate-400 italic">"{trace.review_decision.notes}"</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Batch Participation */}
      {trace.batch_participation?.participated && (
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <h5 className="text-xs text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-2">
            <ListChecks className="h-3 w-3" />
            Batch Operation
          </h5>
          <p className="text-xs text-slate-300">
            This insight was processed as part of a batch operation.
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Batch ID: {trace.batch_participation.batch_id}
          </p>
        </div>
      )}
      
      {/* Action Linkage */}
      {trace.linked_action && (
        <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
          <h5 className="text-xs text-indigo-400 uppercase tracking-wider mb-2 flex items-center gap-2">
            <Zap className="h-3 w-3" />
            Linked Action
          </h5>
          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Action Type</span>
              <span className="text-slate-300">{trace.linked_action.action_type}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Mode</span>
              <Badge className="text-[10px] bg-slate-500/20 border-slate-500/30">
                {trace.linked_action.mode}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Created</span>
              <span className="text-slate-300">{formatRelativeTime(trace.action_created_at)}</span>
            </div>
            {trace.linked_action.mode === 'grouped' && trace.linked_action.source_insight_ids?.length > 1 && (
              <p className="text-slate-500 pt-2 border-t border-white/[0.05]">
                Grouped with {trace.linked_action.source_insight_ids.length - 1} other insight(s)
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Event Counts Summary */}
      {Object.keys(trace.event_counts || {}).length > 0 && (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <CalendarClock className="h-3 w-3" />
          <span>
            {Object.values(trace.event_counts).reduce((a, b) => a + b, 0)} total events
          </span>
        </div>
      )}
    </div>
  );
}

function InsightDetailDialog({ insight, open, onClose }) {
  const [activeTab, setActiveTab] = useState('details');
  
  if (!insight) return null;
  
  const typeConf = insightTypeConfig[insight.insight_type] || insightTypeConfig.high_performer;
  const sevConf = severityConfig[insight.severity] || severityConfig.info;
  const confConf = confidenceConfig[insight.confidence_label] || confidenceConfig.medium;
  const actionConf = actionTypeConfig[insight.recommended_action_type] || actionTypeConfig.monitor_only;
  
  const TypeIcon = typeConf.icon;
  const ConfIcon = confConf.icon;
  const ActionIcon = actionConf.icon;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-white/10 max-w-lg max-h-[80vh] overflow-y-auto" data-testid="insight-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className={cn("p-2 rounded-lg", typeConf.bgClass)}>
              <TypeIcon className={cn("h-5 w-5", typeConf.color)} />
            </div>
            <div>
              <span className="text-slate-100">{insight.headline}</span>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={cn("text-[10px] border", typeConf.bgClass)}>
                  {typeConf.label}
                </Badge>
                <Badge className={cn("text-[10px] border", sevConf.bgClass)}>
                  {sevConf.label} Severity
                </Badge>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        {/* MA-ARCH-158: Tabbed Interface */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-3 bg-slate-800/50">
            <TabsTrigger value="details" className="text-xs data-[state=active]:bg-slate-700" data-testid="tab-details">
              <Info className="h-3 w-3 mr-1" />
              Details
            </TabsTrigger>
            <TabsTrigger value="history" className="text-xs data-[state=active]:bg-slate-700" data-testid="tab-history">
              <History className="h-3 w-3 mr-1" />
              History
            </TabsTrigger>
            <TabsTrigger value="trace" className="text-xs data-[state=active]:bg-slate-700" data-testid="tab-trace">
              <Network className="h-3 w-3 mr-1" />
              Trace
            </TabsTrigger>
          </TabsList>
          
          {/* Details Tab */}
          <TabsContent value="details" className="space-y-4 mt-4">
          {/* Agent */}
          {insight.agent_name && (
            <div className="flex items-center gap-2 p-2 rounded bg-white/[0.02]">
              <User className="h-4 w-4 text-slate-400" />
              <span className="text-sm text-slate-300">{insight.agent_name}</span>
            </div>
          )}
          
          {/* Summary */}
          <p className="text-sm text-slate-300">{insight.summary}</p>
          
          {/* Confidence */}
          <div className="flex items-center gap-2">
            <ConfIcon className={cn("h-4 w-4", confConf.color)} />
            <span className="text-sm text-slate-400">
              Confidence: <span className={confConf.color}>{confConf.label}</span> ({(insight.confidence_score * 100).toFixed(0)}%)
            </span>
          </div>
          
          {/* Key Metrics */}
          <div className="space-y-2">
            <h5 className="text-xs text-slate-500 uppercase tracking-wider">Key Metrics</h5>
            <div className="p-3 rounded bg-white/[0.02] border border-white/[0.05]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400">{insight.metric_primary_name?.replace(/_/g, ' ')}</span>
                <span className="text-sm font-medium text-slate-200">
                  {insight.metric_primary_formatted || insight.metric_primary_value}
                </span>
              </div>
              {insight.baseline_name && (
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Baseline ({insight.baseline_name?.replace(/_/g, ' ')})</span>
                  <span>{insight.baseline_formatted || insight.baseline_value}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Explanation Factors */}
          {insight.explanation_factors?.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs text-slate-500 uppercase tracking-wider">Why This Insight</h5>
              <div className="space-y-1">
                {insight.explanation_factors.map((factor, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 rounded bg-white/[0.02] text-xs">
                    <Info className="h-3 w-3 text-slate-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">{factor.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Supporting Segments */}
          {insight.supporting_segments?.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs text-slate-500 uppercase tracking-wider">Supporting Segments</h5>
              <div className="space-y-1">
                {insight.supporting_segments.map((seg, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 rounded bg-white/[0.02] text-xs">
                    <span className="text-slate-300">{seg.segment_name}</span>
                    <span className="text-slate-400">{seg.metric_name}: {seg.metric_value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* MA-ARCH-155: Normalization Details */}
          {insight.normalization_quality_label && (
            <div className="space-y-2">
              <h5 className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-2">
                <Scale className="h-3 w-3" />
                Context Normalization
              </h5>
              <div className="p-3 rounded bg-white/[0.02] border border-white/[0.05] space-y-2">
                {/* Quality and Scope */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">Normalization Quality</span>
                  <Badge className={cn(
                    "text-[10px] border",
                    (normalizationQualityConfig[insight.normalization_quality_label] || normalizationQualityConfig.insufficient_context_match).bgClass
                  )}>
                    {(normalizationQualityConfig[insight.normalization_quality_label] || normalizationQualityConfig.insufficient_context_match).label}
                  </Badge>
                </div>
                
                {insight.baseline_scope_used && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">Baseline Scope</span>
                    <span className="text-xs text-slate-300">{insight.baseline_scope_used.replace(/_/g, ' ')}</span>
                  </div>
                )}
                
                {insight.comparison_context_count && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">Comparable Peers</span>
                    <span className="text-xs text-slate-300">{insight.comparison_context_count}</span>
                  </div>
                )}
                
                {insight.context_difficulty_label && insight.context_difficulty_label !== 'unknown' && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">Context Difficulty</span>
                    <span className={cn(
                      "text-xs",
                      (contextDifficultyConfig[insight.context_difficulty_label] || contextDifficultyConfig.unknown).color
                    )}>
                      {(contextDifficultyConfig[insight.context_difficulty_label] || contextDifficultyConfig.unknown).label}
                    </span>
                  </div>
                )}
                
                {/* Adjusted Metrics */}
                {(insight.adjusted_close_rate_index || insight.adjusted_l2a_rate_index || insight.adjusted_revenue_index) && (
                  <div className="pt-2 border-t border-white/[0.05] space-y-1">
                    <p className="text-xs text-slate-500 mb-1">Adjusted Index (1.00 = baseline)</p>
                    {insight.adjusted_close_rate_index && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">Close Rate</span>
                        <span className={cn(
                          "text-xs font-medium",
                          insight.adjusted_close_rate_index >= 1.0 ? "text-emerald-400" : "text-amber-400"
                        )}>
                          {insight.adjusted_close_rate_index.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {insight.adjusted_l2a_rate_index && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">L2A Rate</span>
                        <span className={cn(
                          "text-xs font-medium",
                          insight.adjusted_l2a_rate_index >= 1.0 ? "text-emerald-400" : "text-amber-400"
                        )}>
                          {insight.adjusted_l2a_rate_index.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {insight.adjusted_revenue_index && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">Revenue</span>
                        <span className={cn(
                          "text-xs font-medium",
                          insight.adjusted_revenue_index >= 1.0 ? "text-emerald-400" : "text-amber-400"
                        )}>
                          {insight.adjusted_revenue_index.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Raw vs Adjusted Conclusion */}
                {insight.raw_vs_adjusted_conclusion && (
                  <div className="pt-2 border-t border-white/[0.05]">
                    <p className="text-xs text-slate-400">
                      {insight.raw_vs_adjusted_conclusion.replace(/_/g, ' ')}
                    </p>
                  </div>
                )}
                
                {/* Normalization Notes */}
                {insight.normalization_notes && (
                  <div className="pt-2 border-t border-white/[0.05]">
                    <p className="text-xs text-slate-300 italic">{insight.normalization_notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* MA-ARCH-156: Field Quality Details - Only show when relevant */}
          {insight.field_quality_score != null && insight.field_quality_confidence >= 0.4 && (
            <div className="space-y-2">
              <h5 className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-2">
                <Target className="h-3 w-3" />
                Execution Quality Assessment
              </h5>
              <div className="p-3 rounded bg-white/[0.02] border border-white/[0.05] space-y-2">
                {/* Overall Score */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">Field Quality Score</span>
                  <span className={cn(
                    "text-sm font-medium",
                    insight.field_quality_score >= 80 ? "text-emerald-400" :
                    insight.field_quality_score >= 60 ? "text-amber-400" :
                    "text-red-400"
                  )}>
                    {insight.field_quality_score.toFixed(1)}
                  </span>
                </div>
                
                {/* Confidence & Coverage */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">Confidence</span>
                  <Badge className={cn(
                    "text-[10px] border",
                    insight.field_quality_confidence_level === 'high' ? "bg-emerald-500/20 border-emerald-500/30" :
                    insight.field_quality_confidence_level === 'medium' ? "bg-amber-500/20 border-amber-500/30" :
                    "bg-slate-500/20 border-slate-500/30"
                  )}>
                    {insight.field_quality_confidence_level}
                    {insight.field_quality_confidence != null && ` (${(insight.field_quality_confidence * 100).toFixed(0)}%)`}
                  </Badge>
                </div>
                
                {insight.field_quality_sample_size && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">Sample Size</span>
                    <span className="text-xs text-slate-300">{insight.field_quality_sample_size} interactions</span>
                  </div>
                )}
                
                {/* Component Scores */}
                {(insight.field_quality_adherence != null || insight.field_quality_data_capture != null || 
                  insight.field_quality_progression != null) && (
                  <div className="pt-2 border-t border-white/[0.05] space-y-1">
                    <p className="text-xs text-slate-500 mb-1">Component Scores</p>
                    {insight.field_quality_adherence != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">Script Adherence</span>
                        <span className={cn(
                          "text-xs font-medium",
                          insight.field_quality_adherence >= 80 ? "text-emerald-400" :
                          insight.field_quality_adherence >= 60 ? "text-amber-400" : "text-red-400"
                        )}>
                          {insight.field_quality_adherence.toFixed(0)}
                        </span>
                      </div>
                    )}
                    {insight.field_quality_data_capture != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">Data Capture</span>
                        <span className={cn(
                          "text-xs font-medium",
                          insight.field_quality_data_capture >= 80 ? "text-emerald-400" :
                          insight.field_quality_data_capture >= 60 ? "text-amber-400" : "text-red-400"
                        )}>
                          {insight.field_quality_data_capture.toFixed(0)}
                        </span>
                      </div>
                    )}
                    {insight.field_quality_progression != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">Progression</span>
                        <span className={cn(
                          "text-xs font-medium",
                          insight.field_quality_progression >= 80 ? "text-emerald-400" :
                          insight.field_quality_progression >= 60 ? "text-amber-400" : "text-red-400"
                        )}>
                          {insight.field_quality_progression.toFixed(0)}
                        </span>
                      </div>
                    )}
                    {insight.field_quality_objection_handling != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">Objection Handling</span>
                        <span className={cn(
                          "text-xs font-medium",
                          insight.field_quality_objection_handling >= 80 ? "text-emerald-400" :
                          insight.field_quality_objection_handling >= 60 ? "text-amber-400" : "text-red-400"
                        )}>
                          {insight.field_quality_objection_handling.toFixed(0)}
                        </span>
                      </div>
                    )}
                    {insight.field_quality_responsiveness != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">Responsiveness</span>
                        <span className={cn(
                          "text-xs font-medium",
                          insight.field_quality_responsiveness >= 80 ? "text-emerald-400" :
                          insight.field_quality_responsiveness >= 60 ? "text-amber-400" : "text-red-400"
                        )}>
                          {insight.field_quality_responsiveness.toFixed(0)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Top Weaknesses */}
                {insight.field_quality_top_weaknesses && insight.field_quality_top_weaknesses.length > 0 && (
                  <div className="pt-2 border-t border-white/[0.05]">
                    <p className="text-xs text-slate-500 mb-1">Top Weaknesses</p>
                    <div className="space-y-1">
                      {insight.field_quality_top_weaknesses.slice(0, 3).map((weakness, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="text-slate-400">{weakness.dimension?.replace(/_/g, ' ')}</span>
                          <span className="text-red-400">{weakness.avg_score?.toFixed(0) || 'N/A'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Diagnostic Conclusion */}
                {insight.diagnostic_conclusion && (
                  <div className="pt-2 border-t border-white/[0.05]">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">Diagnosis</span>
                      <Badge className={cn(
                        "text-[10px] border",
                        insight.diagnostic_conclusion === 'coaching_issue' ? "bg-violet-500/20 border-violet-500/30 text-violet-400" :
                        insight.diagnostic_conclusion === 'dangerous_success' ? "bg-amber-500/20 border-amber-500/30 text-amber-400" :
                        insight.diagnostic_conclusion === 'strong_execution_underperformance' ? "bg-amber-500/20 border-amber-500/30 text-amber-400" :
                        "bg-slate-500/20 border-slate-500/30"
                      )}>
                        {insight.diagnostic_conclusion.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    {insight.diagnostic_conclusion_reason && (
                      <p className="text-xs text-slate-400 mt-1 italic">{insight.diagnostic_conclusion_reason}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Recommended Action */}
          <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
            <h5 className="text-xs text-indigo-400 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Zap className="h-3 w-3" />
              Recommended Action
            </h5>
            <div className="flex items-center gap-2">
              <ActionIcon className={cn("h-5 w-5", actionConf.color)} />
              <span className="text-sm font-medium text-slate-200">{insight.recommended_action_label}</span>
            </div>
            {insight.requires_approval && (
              <p className="text-xs text-slate-400 mt-2">
                <Shield className="h-3 w-3 inline mr-1" />
                Requires approval before execution
              </p>
            )}
          </div>
          </TabsContent>
          
          {/* History Tab - MA-ARCH-158 */}
          <TabsContent value="history" className="mt-4">
            <InsightHistoryTab insightId={insight.id} />
          </TabsContent>
          
          {/* Decision Trace Tab - MA-ARCH-158 */}
          <TabsContent value="trace" className="mt-4">
            <InsightTraceTab insightId={insight.id} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// MAIN COMPONENT - MA-ARCH-154 Enhanced
// =============================================================================

export default function PerformanceInsights({ period = 'mtd', agentId = null }) {
  const { selectedTenantId } = useTenant();
  const queryClient = useQueryClient();
  
  // Filters
  const [insightTypeFilter, setInsightTypeFilter] = useState('all');
  const [confidenceFilter, setConfidenceFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [actionTypeFilter, setActionTypeFilter] = useState('all');
  const [reviewStatusFilter, setReviewStatusFilter] = useState('all');
  const [includeDismissed, setIncludeDismissed] = useState(false);
  
  // Detail dialog
  const [selectedInsight, setSelectedInsight] = useState(null);
  
  // Review action states
  const [reviewNotes, setReviewNotes] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, insight: null });
  
  // MA-ARCH-157: Batch selection state
  const [selectedInsightIds, setSelectedInsightIds] = useState(new Set());
  const [batchMode, setBatchMode] = useState('grouped'); // 'grouped' or 'individual'
  const [batchValidation, setBatchValidation] = useState(null);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [batchDialogType, setBatchDialogType] = useState(null); // 'approve', 'dismiss', 'create_action'
  
  // Fetch insights
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['performance-insights', period, insightTypeFilter, confidenceFilter, severityFilter, actionTypeFilter, reviewStatusFilter, includeDismissed, selectedTenantId],
    queryFn: () => fetchInsights({
      period,
      insight_type: insightTypeFilter !== 'all' ? insightTypeFilter : undefined,
      confidence: confidenceFilter !== 'all' ? confidenceFilter : undefined,
      severity: severityFilter !== 'all' ? severityFilter : undefined,
      action_type: actionTypeFilter !== 'all' ? actionTypeFilter : undefined,
      review_status: reviewStatusFilter !== 'all' ? reviewStatusFilter : undefined,
      include_dismissed: includeDismissed,
      tenant_id: selectedTenantId,
    }),
    staleTime: 60000,
  });
  
  // MA-ARCH-160: Fetch normalization coverage (filtered universe)
  // MA-PHASE11A-FINAL-APPROVAL-VALIDATION-GATE-023: Use api client to get X-Account-ID header
  const { data: coverageData } = useQuery({
    queryKey: ['normalization-coverage', period, reviewStatusFilter, selectedTenantId],
    queryFn: async () => {
      const params = new URLSearchParams({ period });
      if (reviewStatusFilter !== 'all') params.set('review_status', reviewStatusFilter);
      
      try {
        const resp = await api.get(`/analytics/agent-performance/normalization-coverage?${params}`);
        return resp.data;
      } catch (error) {
        // Silent fail if endpoint not available or permission denied
        if (error.response?.status === 404 || error.response?.status === 403) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 60000,
    retry: false,
  });
  
  // MA-ARCH-154: Review mutations
  const approveMutation = useMutation({
    mutationFn: ({ insightId, notes }) => approveInsight(insightId, notes),
    onSuccess: (data) => {
      toast.success('Insight approved', { description: 'Ready for action creation' });
      queryClient.invalidateQueries(['performance-insights']);
      setConfirmDialog({ open: false, type: null, insight: null });
      setReviewNotes('');
    },
    onError: (error) => {
      toast.error('Failed to approve insight', { description: error.response?.data?.detail || error.message });
    },
  });
  
  const dismissMutation = useMutation({
    mutationFn: ({ insightId, notes }) => dismissInsight(insightId, notes),
    onSuccess: (data) => {
      toast.success('Insight dismissed', { description: 'Hidden from default view' });
      queryClient.invalidateQueries(['performance-insights']);
      setConfirmDialog({ open: false, type: null, insight: null });
      setReviewNotes('');
    },
    onError: (error) => {
      toast.error('Failed to dismiss insight', { description: error.response?.data?.detail || error.message });
    },
  });
  
  const createActionMutation = useMutation({
    mutationFn: ({ insightId }) => createActionFromInsight(insightId),
    onSuccess: (data) => {
      toast.success('Action created', { 
        description: `Action ID: ${data.action_id}`,
        action: {
          label: 'View Actions',
          onClick: () => window.location.href = '/actions',
        },
      });
      queryClient.invalidateQueries(['performance-insights']);
    },
    onError: (error) => {
      toast.error('Failed to create action', { description: error.response?.data?.detail || error.message });
    },
  });
  
  // MA-ARCH-157: Batch mutations
  const batchValidateMutation = useMutation({
    mutationFn: ({ insightIds, operation, mode }) => validateBatch(insightIds, operation, mode),
    onSuccess: (data) => {
      setBatchValidation(data);
    },
    onError: (error) => {
      toast.error('Validation failed', { description: error.response?.data?.detail || error.message });
      setBatchValidation(null);
    },
  });
  
  const batchApproveMutation = useMutation({
    mutationFn: ({ insightIds, notes }) => batchApproveInsights(insightIds, notes),
    onSuccess: (data) => {
      toast.success(`Batch approved: ${data.approved}/${data.total} insights`);
      queryClient.invalidateQueries(['performance-insights']);
      setBatchDialogOpen(false);
      setSelectedInsightIds(new Set());
      setReviewNotes('');
      setBatchValidation(null);
    },
    onError: (error) => {
      toast.error('Batch approve failed', { description: error.response?.data?.detail?.message || error.message });
    },
  });
  
  const batchDismissMutation = useMutation({
    mutationFn: ({ insightIds, notes }) => batchDismissInsights(insightIds, notes),
    onSuccess: (data) => {
      toast.success(`Batch dismissed: ${data.dismissed}/${data.total} insights`);
      queryClient.invalidateQueries(['performance-insights']);
      setBatchDialogOpen(false);
      setSelectedInsightIds(new Set());
      setReviewNotes('');
      setBatchValidation(null);
    },
    onError: (error) => {
      toast.error('Batch dismiss failed', { description: error.response?.data?.detail?.message || error.message });
    },
  });
  
  const batchCreateActionMutation = useMutation({
    mutationFn: ({ insightIds, mode, notes }) => batchCreateActions(insightIds, mode, notes),
    onSuccess: (data) => {
      const modeLabel = data.mode === 'grouped' ? 'grouped action' : `${data.actions_created} individual actions`;
      toast.success(`Created ${modeLabel} from ${data.total_insights} insights`, {
        action: {
          label: 'View Actions',
          onClick: () => window.location.href = '/actions',
        },
      });
      queryClient.invalidateQueries(['performance-insights']);
      setBatchDialogOpen(false);
      setSelectedInsightIds(new Set());
      setReviewNotes('');
      setBatchValidation(null);
    },
    onError: (error) => {
      toast.error('Batch action creation failed', { description: error.response?.data?.detail?.message || error.message });
    },
  });
  
  // MA-ARCH-157: Batch selection handlers
  const handleToggleInsightSelection = (insightId) => {
    setSelectedInsightIds(prev => {
      const next = new Set(prev);
      if (next.has(insightId)) {
        next.delete(insightId);
      } else {
        next.add(insightId);
      }
      return next;
    });
  };
  
  const handleSelectAllEligible = () => {
    const eligibleIds = (insights || [])
      .filter(i => isInsightEligibleForBatch(i))
      .slice(0, 50) // Max batch size
      .map(i => i.id);
    setSelectedInsightIds(new Set(eligibleIds));
  };
  
  const handleClearSelection = () => {
    setSelectedInsightIds(new Set());
    setBatchValidation(null);
  };
  
  const handleOpenBatchDialog = async (type) => {
    if (selectedInsightIds.size === 0) return;
    
    setBatchDialogType(type);
    setBatchDialogOpen(true);
    
    // Trigger validation
    batchValidateMutation.mutate({
      insightIds: Array.from(selectedInsightIds),
      operation: type,
      mode: batchMode,
    });
  };
  
  const handleConfirmBatchAction = () => {
    const insightIds = Array.from(selectedInsightIds);
    
    if (batchDialogType === 'approve') {
      batchApproveMutation.mutate({ insightIds, notes: reviewNotes || null });
    } else if (batchDialogType === 'dismiss') {
      batchDismissMutation.mutate({ insightIds, notes: reviewNotes || null });
    } else if (batchDialogType === 'create_action') {
      batchCreateActionMutation.mutate({ 
        insightIds, 
        mode: batchValidation?.recommended_mode || batchMode,
        notes: reviewNotes || null,
      });
    }
  };
  
  const handleApprove = (insight) => {
    setConfirmDialog({ open: true, type: 'approve', insight });
  };
  
  const handleDismiss = (insight) => {
    setConfirmDialog({ open: true, type: 'dismiss', insight });
  };
  
  const handleCreateAction = (insight) => {
    createActionMutation.mutate({ insightId: insight.id });
  };
  
  const handleConfirmAction = () => {
    const { type, insight } = confirmDialog;
    if (type === 'approve') {
      approveMutation.mutate({ insightId: insight.id, notes: reviewNotes || null });
    } else if (type === 'dismiss') {
      dismissMutation.mutate({ insightId: insight.id, notes: reviewNotes || null });
    }
  };
  
  const isActionLoading = approveMutation.isPending || dismissMutation.isPending || createActionMutation.isPending ||
    batchValidateMutation.isPending || batchApproveMutation.isPending || batchDismissMutation.isPending || batchCreateActionMutation.isPending;
  
  if (error) {
    // MA-PHASE11A-WARROOM-FINAL-VALIDATION-021: Neutral error state
    return (
      <Card className="glass border-white/10">
        <CardContent className="py-12 text-center">
          <Lightbulb className="h-10 w-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-medium">Performance Insights Unavailable</p>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Unable to load performance insights at this time.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  const { summary, insights = [] } = data || {};
  const reviewCounts = summary?.review_counts || {};
  
  return (
    <div className="space-y-4" data-testid="performance-insights">
      {/* Header */}
      <Card className="glass border-white/10">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-amber-400" />
                Performance Insights
              </CardTitle>
              <CardDescription className="text-slate-400">
                Structured recommendations from agent performance data
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {/* MA-ARCH-154: Show Dismissed Toggle */}
              <div className="flex items-center gap-2 mr-2">
                <Switch
                  id="show-dismissed"
                  checked={includeDismissed}
                  onCheckedChange={setIncludeDismissed}
                  className="data-[state=checked]:bg-slate-600"
                />
                <Label htmlFor="show-dismissed" className="text-xs text-slate-400 cursor-pointer">
                  {includeDismissed ? <Eye className="h-3 w-3 inline mr-1" /> : <EyeOff className="h-3 w-3 inline mr-1" />}
                  Dismissed
                </Label>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => refetch()}
                disabled={isFetching}
                className="h-8 px-2"
              >
                <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
              </Button>
            </div>
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Select value={insightTypeFilter} onValueChange={setInsightTypeFilter}>
              <SelectTrigger className="w-40 h-8 text-xs bg-white/5 border-white/10">
                <SelectValue placeholder="Insight Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="high_performer">High Performer</SelectItem>
                <SelectItem value="underperformer">Underperformer</SelectItem>
                <SelectItem value="routing_opportunity">Routing</SelectItem>
                <SelectItem value="coaching_opportunity">Coaching</SelectItem>
                <SelectItem value="script_strength">Script Strength</SelectItem>
                <SelectItem value="script_weakness">Script Weakness</SelectItem>
                <SelectItem value="followup_speed_issue">Speed Issue</SelectItem>
                <SelectItem value="data_capture_issue">Data Capture</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={confidenceFilter} onValueChange={setConfidenceFilter}>
              <SelectTrigger className="w-32 h-8 text-xs bg-white/5 border-white/10">
                <SelectValue placeholder="Confidence" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Confidence</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-32 h-8 text-xs bg-white/5 border-white/10">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="info">Info</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={actionTypeFilter} onValueChange={setActionTypeFilter}>
              <SelectTrigger className="w-36 h-8 text-xs bg-white/5 border-white/10">
                <SelectValue placeholder="Action Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="coach_agent">Coach Agent</SelectItem>
                <SelectItem value="route_more_leads">Route More</SelectItem>
                <SelectItem value="investigate_input_quality">Investigate</SelectItem>
                <SelectItem value="review_script_usage">Review Script</SelectItem>
                <SelectItem value="monitor_only">Monitor Only</SelectItem>
              </SelectContent>
            </Select>
            
            {/* MA-ARCH-154: Review Status Filter */}
            <Select value={reviewStatusFilter} onValueChange={setReviewStatusFilter}>
              <SelectTrigger className="w-36 h-8 text-xs bg-white/5 border-white/10">
                <SelectValue placeholder="Review Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending_review">
                  Pending ({reviewCounts.pending_review || 0})
                </SelectItem>
                <SelectItem value="approved">
                  Approved ({reviewCounts.approved || 0})
                </SelectItem>
                <SelectItem value="action_created">
                  Action Created ({reviewCounts.action_created || 0})
                </SelectItem>
                <SelectItem value="dismissed">
                  Dismissed ({reviewCounts.dismissed || 0})
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
      </Card>
      
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <>
          {/* Summary Cards - Enhanced with review counts */}
          <InsightsSummaryCards summary={summary} />
          
          {/* MA-ARCH-154: Review Status Summary Bar */}
          {summary && (
            <div className="flex items-center gap-3 text-xs">
              <span className="text-slate-500">Review Status:</span>
              <Badge className="bg-amber-500/20 border-amber-500/30 text-amber-400">
                <CircleDot className="h-2.5 w-2.5 mr-1" />
                {reviewCounts.pending_review || 0} Pending
              </Badge>
              <Badge className="bg-emerald-500/20 border-emerald-500/30 text-emerald-400">
                <CheckCircle2 className="h-2.5 w-2.5 mr-1" />
                {reviewCounts.approved || 0} Approved
              </Badge>
              <Badge className="bg-cyan-500/20 border-cyan-500/30 text-cyan-400">
                <Zap className="h-2.5 w-2.5 mr-1" />
                {reviewCounts.action_created || 0} Actions
              </Badge>
              {includeDismissed && (
                <Badge className="bg-slate-500/20 border-slate-500/30 text-slate-400">
                  <Ban className="h-2.5 w-2.5 mr-1" />
                  {reviewCounts.dismissed || 0} Dismissed
                </Badge>
              )}
            </div>
          )}
          
          {/* MA-ARCH-160: Normalization Coverage Summary */}
          {coverageData?.success && coverageData.coverage && (
            <NormalizationCoverageBar coverage={coverageData.coverage} />
          )}
          
          {/* Insights Feed */}
          {insights.length === 0 ? (
            <Card className="glass border-white/10">
              <CardContent className="py-8 text-center">
                <Lightbulb className="h-10 w-10 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-400">No insights found</p>
                <p className="text-xs text-slate-500 mt-1">
                  {insightTypeFilter !== 'all' || confidenceFilter !== 'all' || severityFilter !== 'all' || actionTypeFilter !== 'all' || reviewStatusFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Insights will appear when there is enough agent activity data'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* MA-ARCH-157: Batch Selection Toolbar */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]" data-testid="batch-toolbar">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSelectAllEligible}
                    className="h-8 text-xs"
                    disabled={insights.filter(i => isInsightEligibleForBatch(i)).length === 0}
                  >
                    <ListChecks className="h-3.5 w-3.5 mr-1" />
                    Select Eligible ({insights.filter(i => isInsightEligibleForBatch(i)).length})
                  </Button>
                  {selectedInsightIds.size > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearSelection}
                      className="h-8 text-xs text-slate-400"
                    >
                      <X className="h-3.5 w-3.5 mr-1" />
                      Clear
                    </Button>
                  )}
                  {selectedInsightIds.size > 0 && (
                    <Badge className="bg-blue-500/20 border-blue-500/30 text-blue-400">
                      {selectedInsightIds.size} selected
                    </Badge>
                  )}
                </div>
                
                {selectedInsightIds.size > 0 && (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenBatchDialog('approve')}
                      disabled={isActionLoading}
                      className="h-8 text-xs border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                      data-testid="batch-approve-btn"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                      Approve ({selectedInsightIds.size})
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenBatchDialog('dismiss')}
                      disabled={isActionLoading}
                      className="h-8 text-xs border-slate-500/30 text-slate-400 hover:bg-slate-500/10"
                      data-testid="batch-dismiss-btn"
                    >
                      <Ban className="h-3.5 w-3.5 mr-1" />
                      Dismiss
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenBatchDialog('create_action')}
                      disabled={isActionLoading || insights.filter(i => selectedInsightIds.has(i.id) && i.review_status === 'approved').length === 0}
                      className="h-8 text-xs border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                      data-testid="batch-create-action-btn"
                    >
                      <Zap className="h-3.5 w-3.5 mr-1" />
                      Create Action
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="grid gap-3 md:grid-cols-2">
                {insights.map((insight) => {
                  const isEligible = isInsightEligibleForBatch(insight);
                  const eligibilityReason = getEligibilityReason(insight);
                  const isSelected = selectedInsightIds.has(insight.id);
                  
                  return (
                    <div key={insight.id} className="relative group">
                      {/* MA-ARCH-157: Selection Checkbox */}
                      <div className="absolute top-3 left-3 z-10">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <Checkbox
                                  id={`select-${insight.id}`}
                                  checked={isSelected}
                                  onCheckedChange={() => isEligible && handleToggleInsightSelection(insight.id)}
                                  disabled={!isEligible}
                                  className={cn(
                                    "h-5 w-5 border-2 transition-opacity",
                                    isEligible 
                                      ? "border-blue-500/50 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500" 
                                      : "border-slate-600 opacity-40 cursor-not-allowed",
                                    isSelected && "bg-blue-500 border-blue-500"
                                  )}
                                  data-testid={`insight-checkbox-${insight.id}`}
                                />
                              </div>
                            </TooltipTrigger>
                            {!isEligible && eligibilityReason && (
                              <TooltipContent side="right">
                                <p className="text-xs">{eligibilityReason}</p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      
                      <InsightCard
                        insight={insight}
                        onViewDetails={setSelectedInsight}
                        onApprove={handleApprove}
                        onDismiss={handleDismiss}
                        onCreateAction={handleCreateAction}
                        isActionLoading={isActionLoading}
                        isSelected={isSelected}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
      
      {/* Insight Detail Dialog */}
      <InsightDetailDialog
        insight={selectedInsight}
        open={!!selectedInsight}
        onClose={() => setSelectedInsight(null)}
      />
      
      {/* MA-ARCH-154: Confirm Action Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, type: null, insight: null })}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmDialog.type === 'approve' ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                  <span>Approve Insight</span>
                </>
              ) : (
                <>
                  <Ban className="h-5 w-5 text-slate-400" />
                  <span>Dismiss Insight</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {confirmDialog.insight && (
              <div className="p-3 rounded bg-white/[0.02] border border-white/[0.05]">
                <p className="text-sm font-medium text-slate-200">{confirmDialog.insight.headline}</p>
                <p className="text-xs text-slate-400 mt-1">{confirmDialog.insight.agent_name}</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-xs text-slate-400">Notes (optional)</Label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder={confirmDialog.type === 'dismiss' ? "Why are you dismissing this insight?" : "Any notes for this approval?"}
                className="bg-white/5 border-white/10 text-sm"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button
              variant="ghost"
              onClick={() => setConfirmDialog({ open: false, type: null, insight: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              disabled={approveMutation.isPending || dismissMutation.isPending}
              className={cn(
                confirmDialog.type === 'approve' 
                  ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30" 
                  : "bg-slate-500/20 text-slate-400 hover:bg-slate-500/30"
              )}
            >
              {(approveMutation.isPending || dismissMutation.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {confirmDialog.type === 'approve' ? 'Approve' : 'Dismiss'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* MA-ARCH-157: Batch Action Dialog */}
      <Dialog open={batchDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setBatchDialogOpen(false);
          setBatchValidation(null);
          setReviewNotes('');
        }
      }}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-lg" data-testid="batch-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {batchDialogType === 'approve' ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                  <span>Batch Approve Insights</span>
                </>
              ) : batchDialogType === 'dismiss' ? (
                <>
                  <Ban className="h-5 w-5 text-slate-400" />
                  <span>Batch Dismiss Insights</span>
                </>
              ) : (
                <>
                  <Zap className="h-5 w-5 text-cyan-400" />
                  <span>Batch Create Actions</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Validation Loading */}
            {batchValidateMutation.isPending && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-blue-400 mr-2" />
                <span className="text-sm text-slate-400">Validating batch...</span>
              </div>
            )}
            
            {/* Validation Results */}
            {batchValidation && (
              <>
                {batchValidation.eligible ? (
                  <div className="space-y-3">
                    {/* Summary */}
                    <div className="p-3 rounded bg-emerald-500/10 border border-emerald-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="h-4 w-4 text-emerald-400" />
                        <span className="text-sm text-emerald-400 font-medium">Batch is valid</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-slate-400">Insights:</span>
                          <span className="text-slate-200 ml-1">{batchValidation.summary?.count}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Avg Confidence:</span>
                          <span className="text-slate-200 ml-1">{((batchValidation.summary?.avg_confidence || 0) * 100).toFixed(0)}%</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Min Confidence:</span>
                          <span className="text-slate-200 ml-1">{((batchValidation.summary?.min_confidence || 0) * 100).toFixed(0)}%</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Target:</span>
                          <span className="text-slate-200 ml-1">{batchValidation.summary?.shared_target_name || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Grouping Rationale */}
                    <div className="p-3 rounded bg-white/[0.02] border border-white/[0.05]">
                      <p className="text-xs text-slate-500 mb-1">Grouping Rationale:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {batchValidation.summary?.shared_insight_type && (
                          <Badge variant="outline" className="text-[10px] text-slate-300 border-slate-600">
                            Type: {insightTypeConfig[batchValidation.summary.shared_insight_type]?.label || batchValidation.summary.shared_insight_type}
                          </Badge>
                        )}
                        {batchValidation.summary?.shared_action_type && (
                          <Badge variant="outline" className="text-[10px] text-slate-300 border-slate-600">
                            Action: {actionTypeConfig[batchValidation.summary.shared_action_type]?.label || batchValidation.summary.shared_action_type}
                          </Badge>
                        )}
                        {batchValidation.summary?.shared_scope_type && (
                          <Badge variant="outline" className="text-[10px] text-slate-300 border-slate-600">
                            Scope: {batchValidation.summary.shared_scope_type}
                          </Badge>
                        )}
                        {batchValidation.summary?.shared_primary_issue && (
                          <Badge variant="outline" className="text-[10px] text-slate-300 border-slate-600">
                            Issue: {batchValidation.summary.shared_primary_issue.replace(/_/g, ' ')}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Warnings */}
                    {batchValidation.summary?.warnings && batchValidation.summary.warnings.length > 0 && (
                      <div className="p-3 rounded bg-amber-500/10 border border-amber-500/20">
                        <div className="flex items-center gap-1 mb-1">
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
                          <span className="text-xs text-amber-400 font-medium">Warnings</span>
                        </div>
                        <ul className="text-xs text-slate-400 space-y-0.5">
                          {batchValidation.summary.warnings.map((w, i) => (
                            <li key={i}>• {w}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Mode Selection for Create Action */}
                    {batchDialogType === 'create_action' && (
                      <div className="p-3 rounded bg-white/[0.02] border border-white/[0.05]">
                        <p className="text-xs text-slate-500 mb-2">Action Creation Mode:</p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={batchValidation.recommended_mode === 'grouped' ? 'default' : 'outline'}
                            onClick={() => setBatchMode('grouped')}
                            disabled={batchValidation.recommended_mode === 'individual'}
                            className={cn(
                              "h-8 text-xs flex-1",
                              batchValidation.recommended_mode === 'grouped' && "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
                            )}
                          >
                            <ListChecks className="h-3.5 w-3.5 mr-1" />
                            Grouped Action
                          </Button>
                          <Button
                            size="sm"
                            variant={batchValidation.recommended_mode === 'individual' || batchMode === 'individual' ? 'default' : 'outline'}
                            onClick={() => setBatchMode('individual')}
                            className={cn(
                              "h-8 text-xs flex-1",
                              (batchValidation.recommended_mode === 'individual' || batchMode === 'individual') && "bg-blue-500/20 text-blue-400 border-blue-500/30"
                            )}
                          >
                            Individual Actions
                          </Button>
                        </div>
                        {batchValidation.mode_fallback_reason && (
                          <p className="text-[10px] text-amber-400 mt-1.5">
                            {batchValidation.mode_fallback_reason}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Notes */}
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-400">Notes (optional)</Label>
                      <Textarea
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        placeholder={batchDialogType === 'dismiss' ? "Why are you dismissing these insights?" : "Any notes for this batch?"}
                        className="bg-white/5 border-white/10 text-sm"
                        rows={2}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="p-3 rounded bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="h-4 w-4 text-red-400" />
                      <span className="text-sm text-red-400 font-medium">Batch validation failed</span>
                    </div>
                    <ul className="text-xs text-slate-400 space-y-1">
                      {batchValidation.blocked_reasons?.slice(0, 5).map((reason, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-red-400">•</span>
                          <span>
                            {reason.insight_id !== 'batch' && <span className="text-slate-500">({reason.insight_id.slice(-6)})</span>}
                            {' '}{reason.description}
                          </span>
                        </li>
                      ))}
                      {batchValidation.blocked_reasons?.length > 5 && (
                        <li className="text-slate-500">...and {batchValidation.blocked_reasons.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setBatchDialogOpen(false);
                setBatchValidation(null);
                setReviewNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmBatchAction}
              disabled={
                !batchValidation?.eligible ||
                batchApproveMutation.isPending ||
                batchDismissMutation.isPending ||
                batchCreateActionMutation.isPending
              }
              className={cn(
                batchDialogType === 'approve' 
                  ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30" 
                  : batchDialogType === 'dismiss'
                  ? "bg-slate-500/20 text-slate-400 hover:bg-slate-500/30"
                  : "bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30"
              )}
            >
              {(batchApproveMutation.isPending || batchDismissMutation.isPending || batchCreateActionMutation.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {batchDialogType === 'approve' 
                ? `Approve ${selectedInsightIds.size}` 
                : batchDialogType === 'dismiss'
                ? `Dismiss ${selectedInsightIds.size}`
                : `Create Action${batchValidation?.recommended_mode === 'individual' ? 's' : ''}`
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =============================================================================
// AGENT INSIGHTS SECTION (for drilldown)
// =============================================================================

export function AgentInsightsSection({ agentId, agentName, period = 'mtd' }) {
  const { selectedTenantId } = useTenant();
  const [selectedInsight, setSelectedInsight] = useState(null);
  const queryClient = useQueryClient();
  
  const { data, isLoading } = useQuery({
    queryKey: ['agent-insights', agentId, period, selectedTenantId],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      queryParams.append('period', period);
      if (selectedTenantId) queryParams.append('tenant_id', selectedTenantId);
      
      const response = await api.get(`/analytics/agent-performance/insights/${agentId}?${queryParams}`);
      return response.data;
    },
    enabled: !!agentId,
    staleTime: 60000,
  });
  
  // Simple handlers for agent section (use toast for feedback)
  const handleApprove = async (insight) => {
    try {
      await approveInsight(insight.id);
      toast.success('Insight approved');
      queryClient.invalidateQueries(['agent-insights']);
    } catch (error) {
      toast.error('Failed to approve insight');
    }
  };
  
  const handleDismiss = async (insight) => {
    try {
      await dismissInsight(insight.id);
      toast.success('Insight dismissed');
      queryClient.invalidateQueries(['agent-insights']);
    } catch (error) {
      toast.error('Failed to dismiss insight');
    }
  };
  
  const handleCreateAction = async (insight) => {
    try {
      const result = await createActionFromInsight(insight.id);
      toast.success(`Action created: ${result.action_id}`);
      queryClient.invalidateQueries(['agent-insights']);
    } catch (error) {
      toast.error('Failed to create action');
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }
  
  const { insights = [] } = data || {};
  
  if (insights.length === 0) {
    return (
      <div className="text-center py-4">
        <Lightbulb className="h-6 w-6 text-slate-600 mx-auto mb-2" />
        <p className="text-xs text-slate-400">No insights for this agent</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      <h5 className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-2">
        <Lightbulb className="h-3 w-3 text-amber-400" />
        Insights ({insights.length})
      </h5>
      <div className="space-y-2">
        {insights.map((insight) => (
          <InsightCard
            key={insight.id}
            insight={insight}
            onViewDetails={setSelectedInsight}
            onApprove={handleApprove}
            onDismiss={handleDismiss}
            onCreateAction={handleCreateAction}
            isActionLoading={false}
          />
        ))}
      </div>
      
      <InsightDetailDialog
        insight={selectedInsight}
        open={!!selectedInsight}
        onClose={() => setSelectedInsight(null)}
      />
    </div>
  );
}
